import { useEffect, useState } from 'react'
import { useApp } from './AppStore'
import type { StatEvent } from '../domain/types'

/**
 * Every stat event in local storage.
 *
 * Stats needs the whole season at once, unlike the live screen which only ever
 * loads one game. A full season of three stats across twenty games is a few
 * thousand small records, so a single read is fine; if that ever stops being
 * true, this is the one place to add an index.
 */
export function useAllEvents(): { events: StatEvent[]; loading: boolean; error: string | null } {
  const { repo, games } = useApp()
  const [events, setEvents] = useState<StatEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Re-read when the set of games changes, which is the only thing that can add
  // or remove events behind this screen's back.
  const gameCount = games.length

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    repo
      .listAllEvents()
      .then((rows) => {
        if (cancelled) return
        setEvents(rows)
        setLoading(false)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : String(err))
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [repo, gameCount])

  return { events, loading, error }
}
